import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiFetch } from './operations';

const STORAGE_KEY = 'eco-experience-favorites';
const FavoritesContext = createContext(null);

const readLocal = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
  catch { return []; }
};

export const FavoritesProvider = ({ children }) => {
  const [favorites, setFavorites] = useState(readLocal);
  const [authenticated, setAuthenticated] = useState(false);

  useEffect(() => {
    apiFetch('/traveller/me')
      .then(() => apiFetch('/traveller/favorites'))
      .then((items) => { setAuthenticated(true); setFavorites(items); })
      .catch(() => setAuthenticated(false));
  }, []);

  const persistLocal = useCallback((items) => {
    setFavorites(items);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, []);

  const toggle = useCallback(async (experience) => {
    if (!experience?.id || !experience?.slug) return;
    const saved = favorites.some((item) => item.id === experience.id);
    const next = saved ? favorites.filter((item) => item.id !== experience.id) : [...favorites, experience];
    if (!authenticated) {
      persistLocal(next);
      return;
    }
    await apiFetch(`/traveller/favorites/${encodeURIComponent(experience.id)}`, { method: saved ? 'DELETE' : 'POST' });
    setFavorites(next);
  }, [authenticated, favorites, persistLocal]);

  const value = useMemo(() => ({ favorites, authenticated, isFavorite: (id) => favorites.some((item) => item.id === id), toggle }), [authenticated, favorites, toggle]);
  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
};

export const useFavorites = () => useContext(FavoritesContext) || { favorites: [], authenticated: false, isFavorite: () => false, toggle: async () => {} };
